<script setup lang="ts">
//#region import
import { isEmpty, merge, omit } from 'lodash-es';
import { nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useSessionAction } from '@/composables/useSessionAction';
import { useTablePaginator, useTableSort } from '@/composables/useTableLS';
import updateUrl from '@/service/updateUrl';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import type { GetControlTowerExternalAccessTokensData, GetControlTowerExternalAccessTokensResponse } from '@/types/openapi-ts';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { SettingsPageQuery } from '@/types/core';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region const
const sizes = {
  720: 5,
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<GetControlTowerExternalAccessTokensResponse['payload'][number]>[] = [
  {
    name: 'name',
    minWidth: '120',
  },
  {
    name: 'service',
    width: '200',
    minWidth: '120',
    computedValue: ({ service }) => SERVICE_MAP[service],
  },
  {
    name: 'created_at',
    width: '200',
    minWidth: '120',
    format: 'date-time',
    sortable: 'custom',
  },
  {
    name: 'last_active',
    width: '200',
    minWidth: '120',
    format: 'date-time',
  },
  {
    name: 'id',
    width: '265',
    minWidth: '120',
    copyValue: true,
  },
];

const SERVICE_MAP = {
  'events-gw': 'Events Gateway',
  api: 'API',
};

const LS_PATH = 'TokenManagement';
//#endregion

//#region composables
const { onSessionUnlock } = useSessionAction();
const route = useRoute();
const api = useApi();
const { t } = useI18n();
const popupManager = usePopupManager();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'created_at',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region state
const tableData = ref<GetControlTowerExternalAccessTokensResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  } as GetControlTowerExternalAccessTokensData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters: SettingsPageQuery<GetControlTowerExternalAccessTokensData['query']> = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  updateUrl.updateGetParams(_dataFilters);
};

const fetchData = () => {
  if(!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  _completedFilterUrl();
  const options = _query();

  api.getAccessTokens(options).then((res) => {
    tableData.value = res.data.payload;
    count.value = res.data.misc!.count;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
};

const initData = () => {
  const _urlData = updateUrl.getParseParams(route.hash);
  if (!isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;

    merge(optionsSort.value, _urlData);
  }else {
    setSort(_urlData);
  }
  nextTick(() => {
    fetchData();
  });
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change']  = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleDeleteToken = (row: GetControlTowerExternalAccessTokensResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.tokens.title_delete_token', { token: row.name }),
      message: t('settings.tokens.msg_delete_token', { token: row.name}),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('settings.tokens.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteAccessToken(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
};

const handleCreateToken = () => {
  popupManager.createToken.open({
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
};
//#endregion

//#region hooks
onSessionUnlock.value = initData;
initData();
//#endregion
</script>

<template>
  <div class="token-management">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.tokens.title') }}
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          filled
          icon="plus"
          :disabled="isDataLoading"
          @click="handleCreateToken"
        >
          {{ $t('settings.tokens.create_token') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      lazy-loading
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      i18n-path="settings.tokens"
      element-loading-custom-class="data-loader-spinner"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      is-static
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="74"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              @click="handleDeleteToken(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang="scss">

</style>
